/**
 * ThreatAssessor — evaluates robot threat levels for enemy decision-making.
 *
 * Computes a threat score per robot based on DPS, range, HP.
 * Provides fuzzy path selection (spawn) and fuzzy aggro targeting.
 *
 * Threat score formula:
 *   threat = dps × rangeFactor × hpFactor
 *   - dps = Σ(damage × fireRate) for non-broken weapon components
 *   - rangeFactor = max(weaponRange) / TILE_SIZE  (normalized tile reach)
 *   - hpFactor = sqrt(currentHp / 100)  (tougher robots hold ground longer)
 */

import { Robot } from '@/entities/Robot';
import { ComponentCategory } from '@/components/ComponentDefs';
import { TILE_SIZE } from '@/config/GameConfig';

// ── Robot threat score ──

export interface ThreatInfo {
  robot: Robot;
  threat: number;  // raw threat score
  dps: number;     // total damage per second
  maxRange: number; // max weapon range (px)
}

/** Compute threat score for a single robot. Cached on robot between recalcs. */
export function computeThreatScore(robot: Robot): ThreatInfo {
  if (!robot.alive) return { robot, threat: 0, dps: 0, maxRange: 0 };

  let dps = 0;
  let maxRange = 0;

  for (const comp of robot.components) {
    if (comp.broken) continue;
    if (comp.def.category !== ComponentCategory.WEAPON) continue;
    dps += comp.def.damage * comp.def.fireRate;
    if (comp.def.weaponRange > maxRange) maxRange = comp.def.weaponRange;
  }

  // No weapons → minimal threat (still a blocker if present)
  if (dps <= 0) return { robot, threat: 0.1, dps: 0, maxRange: 0 };

  const rangeFactor = Math.max(1, maxRange / TILE_SIZE);
  const hpFactor = Math.sqrt(Math.max(1, robot.currentHp) / 100);
  const threat = dps * rangeFactor * hpFactor;

  return { robot, threat, dps, maxRange };
}

// ── Fuzzy path selection at spawn ──

/**
 * Choose a path index using fuzzy threat evaluation.
 * Lower threat paths are more likely to be chosen, but not guaranteed.
 *
 * @param paths     Array of waypoint paths (from PATHS)
 * @param robots    All alive player robots
 * @param rng       Optional random value 0..1 (defaults to Math.random)
 * @returns         Chosen path index
 */
export function chooseFuzzyPath(
  paths: { x: number; y: number }[][],
  robots: Robot[],
  rng: number = Math.random(),
): number {
  if (paths.length === 0) return 0;
  if (paths.length === 1) return 0;

  const robotThreats = robots
    .filter(r => r.alive)
    .map(r => computeThreatScore(r));

  // For each path, compute cumulative threat from nearby robots
  const pathThreats: number[] = [];
  for (let pi = 0; pi < paths.length; pi++) {
    const path = paths[pi];
    let pathThreat = 0;

    for (const ti of robotThreats) {
      if (ti.threat <= 0) continue;
      // Distance = min distance from robot to any waypoint on this path
      const dist = minDistToPath(ti.robot.x, ti.robot.y, path);
      // Robots within their weapon range contribute full threat,
      // further robots decay quadratically
      const effectiveRange = Math.max(ti.maxRange, TILE_SIZE * 2);
      const proximity = dist <= effectiveRange
        ? 1
        : effectiveRange / dist; // linear decay beyond range
      pathThreat += ti.threat * proximity;
    }

    pathThreats.push(pathThreat);
  }

  // Convert threats to selection weights (inverse: low threat → high weight)
  // Add a floor so even the most dangerous path has some chance
  const maxThreat = Math.max(...pathThreats, 1);
  const weights: number[] = pathThreats.map(t => {
    // Invert: safest path gets weight 1.0, most dangerous gets 0.15
    const normalized = t / maxThreat;
    return 0.15 + 0.85 * (1 - normalized);
  });

  // Weighted random selection
  const totalWeight = weights.reduce((s, w) => s + w, 0);
  let roll = rng * totalWeight;
  for (let i = 0; i < weights.length; i++) {
    roll -= weights[i];
    if (roll <= 0) return i;
  }
  return paths.length - 1;
}

// ── Fuzzy aggro targeting ──

/**
 * Fuzzy target selection for enemy aggro.
 * Evaluates 3 priorities:
 *   1. BASE pull (always present, proportional to progress along path)
 *   2. High threat robots (even if far — dangerous turrets)
 *   3. Proximity (nearby robots, even if weak)
 *
 * Returns the position of the chosen target, or null (= continue to base).
 *
 * @param enemyX, enemyY     Enemy position
 * @param robotPositions      Alive robot positions with their Robot references
 * @param aggroRange          Max aggro detection range (px)
 * @param pathProgress        0..1 how far enemy is along its path (0=spawn, 1=base)
 * @returns                   Target position or null (go to base)
 */
export function fuzzyAggroTarget(
  enemyX: number,
  enemyY: number,
  robots: Robot[],
  aggroRange: number,
  pathProgress: number,
): { x: number; y: number } | null {
  if (robots.length === 0) return null;

  // Base pull increases as enemy gets closer to base
  // At spawn (0%) → basePull=0.2, at base (100%) → basePull=0.95
  const basePull = 0.2 + 0.75 * pathProgress;

  // Score each robot
  interface Candidate {
    x: number;
    y: number;
    score: number;
  }
  const candidates: Candidate[] = [];

  for (const robot of robots) {
    if (!robot.alive) continue;
    const dx = robot.x - enemyX;
    const dy = robot.y - enemyY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > aggroRange) continue;

    const ti = computeThreatScore(robot);

    // Proximity score: peaks at close range, decays with distance
    // Normalized 0..1 (1 = touching, 0 = at aggroRange)
    const proxScore = 1 - (dist / aggroRange);

    // Threat score: normalized by a reference DPS (30 DPS = "standard turret")
    const threatScore = Math.min(2, ti.threat / 30);

    // Combined score: weighted blend of proximity and threat
    // High-threat far robot: threatScore dominates
    // Low-threat close robot: proxScore dominates
    const score = proxScore * 0.4 + threatScore * 0.6;

    candidates.push({ x: robot.x, y: robot.y, score });
  }

  if (candidates.length === 0) return null;

  // Best candidate score
  const bestScore = Math.max(...candidates.map(c => c.score));

  // Compare against base pull: if base pull wins, ignore robots
  // Fuzzy: add some noise so it's not deterministic
  const noise = (Math.random() - 0.5) * 0.15;
  if (basePull + noise > bestScore) return null;

  // Fuzzy selection among candidates (weighted by score)
  const totalScore = candidates.reduce((s, c) => s + c.score, 0);
  let roll = Math.random() * totalScore;
  for (const c of candidates) {
    roll -= c.score;
    if (roll <= 0) return { x: c.x, y: c.y };
  }
  return candidates[candidates.length - 1];
}

// ── Helpers ──

/** Min distance from a point to any waypoint on a path. */
function minDistToPath(px: number, py: number, path: { x: number; y: number }[]): number {
  let minD = Infinity;
  for (const wp of path) {
    const dx = px - wp.x;
    const dy = py - wp.y;
    const d = dx * dx + dy * dy;
    if (d < minD) minD = d;
  }
  return Math.sqrt(minD);
}
